// src/pages/ApplyJob.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { requirementApi, applicationApi } from '../../feddback-services/api';
import './ApplyJob.css';

const ApplyJob = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [requirement, setRequirement] = useState(null);
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [fileUploads, setFileUploads] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  // ✅ Get location from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const loc = params.get('location');
    if (loc) {
      setSelectedLocation(loc);
      console.log('📍 Selected location from URL:', loc);
    }
  }, [location]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');
    const userId = localStorage.getItem('userId');
    
    console.log('🔐 User:', { token: !!token, role, userId });
    
    if (!token || role !== 'candidate') {
      navigate('/login');
      return;
    }
    
    if (!id) {
      navigate('/user/view-requirement');
      return;
    }
    
    loadRequirement();
  }, [id]);

  const loadRequirement = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await requirementApi.get(id);
      const data = response.data.data || response.data;
      
      console.log('📋 Requirement loaded:', data);
      
      if (data.status !== 'open') {
        setError('This position is no longer open for applications.');
        setTimeout(() => navigate('/user/view-requirement'), 3000);
        return;
      }
      
      setRequirement(data);
      setFields(data.fields || []);
      
      const initialData = {};
      // ✅ Add location to form data
      if (selectedLocation) {
        initialData.location = selectedLocation;
      }
      
      (data.fields || []).forEach(field => {
        if (field.field_type !== 'file') {
          initialData[field.id] = field.field_type === 'boolean' ? false : '';
        }
      });
      setFormData(initialData);
    } catch (err) {
      console.error('Error loading requirement:', err);
      setError(err.response?.data?.message || 'Failed to load requirement details.');
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    setError('');
  };

  const handleFileChange = (fieldId, file) => {
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
      const maxSize = 10 * 1024 * 1024;
      
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF, DOC, DOCX, JPG, or PNG file');
        return;
      }
      
      if (file.size > maxSize) {
        setError('File size must be less than 10MB');
        return;
      }
      
      setFileUploads(prev => ({ ...prev, [fieldId]: file }));
      setFormData(prev => ({ ...prev, [fieldId]: file.name }));
      setError('');
    }
  };

  const renderField = (field) => {
    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            id={`field-${field.id}`}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={`Enter ${field.field_name}`}
            className="apply-field-input"
            required={field.is_required}
          />
        );

      case 'textarea':
        return (
          <textarea
            id={`field-${field.id}`}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={`Enter ${field.field_name}`}
            className="apply-field-textarea"
            rows="4"
            required={field.is_required}
          />
        );

      case 'email':
        return (
          <input
            type="email"
            id={`field-${field.id}`}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={`Enter ${field.field_name}`}
            className="apply-field-input"
            required={field.is_required}
          />
        );

      case 'phone':
        return (
          <input
            type="tel"
            id={`field-${field.id}`}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={`Enter ${field.field_name}`}
            className="apply-field-input"
            required={field.is_required}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            id={`field-${field.id}`}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={`Enter ${field.field_name}`}
            className="apply-field-input"
            required={field.is_required}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            id={`field-${field.id}`}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className="apply-field-input"
            required={field.is_required}
          />
        );

      case 'dropdown':
        return (
          <select
            id={`field-${field.id}`}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className="apply-field-select"
            required={field.is_required}
          >
            <option value="">Select {field.field_name}</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'boolean':
        return (
          <div className="apply-field-checkbox">
            <input
              type="checkbox"
              id={`field-${field.id}`}
              checked={formData[field.id] || false}
              onChange={(e) => handleFieldChange(field.id, e.target.checked)}
              className="apply-field-checkbox-input"
            />
            <label htmlFor={`field-${field.id}`}>Yes</label>
          </div>
        );

      case 'file':
        return (
          <div className="apply-field-file">
            <input
              type="file"
              id={`field-${field.id}`}
              onChange={(e) => handleFileChange(field.id, e.target.files[0])}
              className="apply-field-file-input"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              required={field.is_required && !fileUploads[field.id]}
            />
            {fileUploads[field.id] && (
              <div className="file-selected">
                <i className="fas fa-check-circle"></i>
                <span>{fileUploads[field.id].name}</span>
                <button 
                  type="button" 
                  className="file-remove"
                  onClick={() => {
                    setFileUploads(prev => {
                      const newFiles = { ...prev };
                      delete newFiles[field.id];
                      return newFiles;
                    });
                    setFormData(prev => ({ ...prev, [field.id]: '' }));
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
            <small>PDF, DOC, DOCX, JPG, PNG (Max 10MB)</small>
          </div>
        );

      default:
        return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      
      console.log('👤 Submitting with userId:', userId);
      console.log('📍 Selected location:', selectedLocation);
      
      if (!token) {
        setError('Please login to apply');
        navigate('/login');
        return;
      }
      
      if (!userId) {
        setError('User ID not found. Please logout and login again.');
        return;
      }

      // Check required fields
      const missingFields = fields.filter(field => 
        field.is_required && 
        (!formData[field.id] || formData[field.id] === '')
      );

      if (missingFields.length > 0) {
        setError(`Please fill in all required fields: ${missingFields.map(f => f.field_name).join(', ')}`);
        setSubmitting(false);
        return;
      }

      // ✅ Prepare answers
      const answersArray = fields
        .filter(field => field.field_type !== 'file' || fileUploads[field.id])
        .map(field => ({
          field_id: field.id,
          value: field.field_type === 'file' ? fileUploads[field.id]?.name || '' : String(formData[field.id] || '')
        }));

      // ✅ Payload with preferred_location
      const payload = {
        requirement_id: id,
        source: 'website',
        preferred_location: selectedLocation || '', // ✅ Save location
        answers: answersArray
      };

      console.log('📤 Submitting payload:', JSON.stringify(payload, null, 2));

      const response = await applicationApi.create(payload);

      if (response.data.success) {
        const applicationId = response.data.data.id;
        
        const fileFields = fields.filter(f => f.field_type === 'file');
        for (const field of fileFields) {
          if (fileUploads[field.id]) {
            const formDataUpload = new FormData();
            formDataUpload.append('file', fileUploads[field.id]);
            await applicationApi.uploadFile(applicationId, field.id, formDataUpload);
          }
        }

        setSuccess('Application submitted successfully!');
        setTimeout(() => {
          navigate('/user/applications');
        }, 2000);
      }
    } catch (err) {
      console.error('❌ Error submitting application:', err);
      console.error('Response data:', err.response?.data);
      console.error('Response status:', err.response?.status);
      
      if (err.response?.status === 400) {
        const errorData = err.response.data;
        if (errorData.message) {
          setError(errorData.message);
        } else if (errorData.errors) {
          const errorMessages = Object.values(errorData.errors).flat().join(', ');
          setError(`Validation failed: ${errorMessages}`);
        } else {
          setError('Invalid data provided. Please check all fields.');
        }
      } else if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        navigate('/login');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to apply.');
      } else {
        setError(err.response?.data?.message || 'Failed to submit application. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getFieldTypeLabel = (type) => {
    const labels = {
      text: 'Text',
      number: 'Number',
      email: 'Email',
      phone: 'Phone',
      date: 'Date',
      dropdown: 'Dropdown',
      textarea: 'Textarea',
      file: 'File',
      boolean: 'Boolean',
    };
    return labels[type] || type;
  };

  const getFieldTypeIcon = (type) => {
    const icons = {
      text: 'fa-font',
      number: 'fa-hashtag',
      email: 'fa-envelope',
      phone: 'fa-phone',
      date: 'fa-calendar',
      dropdown: 'fa-caret-down',
      textarea: 'fa-align-left',
      file: 'fa-file',
      boolean: 'fa-check-square',
    };
    return icons[type] || 'fa-cog';
  };

  if (loading) {
    return (
      <div className="apply-loading">
        <div className="spinner"></div>
        <p>Loading application form...</p>
      </div>
    );
  }

  if (error && !requirement) {
    return (
      <div className="apply-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{error}</p>
        <button onClick={() => navigate('/user/view-requirement')} className="btn-back">
          Back to Jobs
        </button>
      </div>
    );
  }

  return (
    <div className="apply-container">
      <div className="apply-card">
        {/* Header */}
        <div className="apply-header">
          <button className="btn-back" onClick={() => navigate('/user/view-requirement')}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
          <h1>Apply for {requirement?.title}</h1>
          <span className="apply-code">{requirement?.requirement_code}</span>
        </div>

        {/* ✅ Show selected location */}
        {selectedLocation && (
          <div className="apply-location-banner">
            <i className="fas fa-map-pin"></i>
            Interview Location: <strong>{selectedLocation}</strong>
          </div>
        )}

        {/* Application Form */}
        <div className="apply-form-section">
          <h2>
            <i className="fas fa-clipboard-list"></i> Application Form
          </h2>
          
          {error && (
            <div className="apply-error-message">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}
          
          {success && (
            <div className="apply-success-message">
              <i className="fas fa-check-circle"></i>
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="apply-form">
            {/* ✅ Hidden location field */}
            <input type="hidden" name="location" value={selectedLocation || ''} />
            
            {fields.map((field, index) => (
              <div key={index} className="apply-form-group">
                <label className="apply-form-label" htmlFor={`field-${field.id}`}>
                  {field.field_name}
                  {field.is_required && <span className="required-star">*</span>}
                </label>
                {renderField(field)}
                {field.is_required && (
                  <small className="field-hint required-hint">Required</small>
                )}
              </div>
            ))}

            <div className="apply-form-actions">
              <button type="button" className="btn-cancel" onClick={() => navigate('/user/view-requirement')}>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={submitting}>
                <i className={`fas ${submitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplyJob;